<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { isDarkMode, toggleDarkMode } = useLayout()

const label = computed(() =>
  isDarkMode.value
    ? t('pages.user.settings.appearance.switchToLight')
    : t('pages.user.settings.appearance.switchToDark'),
)
</script>

<template>
  <button
    v-app-tooltip.bottom="label"
    type="button"
    role="switch"
    class="theme-toggle"
    :class="{ 'is-dark': isDarkMode }"
    :aria-checked="isDarkMode"
    :aria-label="label"
    @click="toggleDarkMode"
  >
    <!-- One SVG for the whole control: the thumb slides between the two icons and sits *behind*
         them, so whichever icon it covers reads as the active one. Both icons stay fully drawn in
         either state — the switch only works as a signpost if you can still see where it will go.

         The `transform` attribute placing each icon must not be touched from CSS (a CSS transform
         would replace it, not compose with it), so every animated transform goes on the inner
         `.icon-art` / `.sun-rays` groups, which carry no attribute of their own. -->
    <svg viewBox="0 0 56 28" aria-hidden="true" focusable="false">
      <rect class="track" x="0.75" y="0.75" width="54.5" height="26.5" rx="13.25" />

      <circle class="thumb" cx="14" cy="14" r="11" />

      <g class="icon icon-sun" transform="translate(14 14)">
        <g class="icon-art">
          <circle class="sun-core" r="3.4" />
          <g class="sun-rays">
            <line x1="0" y1="-5.4" x2="0" y2="-7.4" />
            <line x1="0" y1="5.4" x2="0" y2="7.4" />
            <line x1="-5.4" y1="0" x2="-7.4" y2="0" />
            <line x1="5.4" y1="0" x2="7.4" y2="0" />
            <line x1="-3.8" y1="-3.8" x2="-5.2" y2="-5.2" />
            <line x1="3.8" y1="3.8" x2="5.2" y2="5.2" />
            <line x1="-3.8" y1="3.8" x2="-5.2" y2="5.2" />
            <line x1="3.8" y1="-3.8" x2="5.2" y2="-5.2" />
          </g>
        </g>
      </g>

      <g class="icon icon-moon" transform="translate(42 14)">
        <g class="icon-art">
          <path d="M2.55 -6.3A6.8 6.8 0 1 0 6 3.19A5.4 5.4 0 0 1 2.55 -6.3Z" />
        </g>
      </g>
    </svg>
  </button>
</template>

<style scoped>
.theme-toggle {
  background: none;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
}

.theme-toggle svg {
  height: 1.75rem;
  width: 3.5rem;
}

.theme-toggle:focus-visible {
  outline: 2px solid var(--ui-primary);
  outline-offset: 2px;
}

.track {
  fill: var(--ui-bg);
  stroke: var(--ui-border);
  stroke-width: 1.5;
  transition: stroke 0.2s;
}

.theme-toggle:hover .track {
  stroke: var(--ui-primary);
}

.thumb {
  fill: var(--ui-primary);
  /* Slight overshoot so the thumb lands with some weight instead of easing out flatly. */
  transition: transform 320ms cubic-bezier(0.4, 0.15, 0.2, 1.2);
}

.theme-toggle.is-dark .thumb {
  transform: translateX(28px);
}

/* Active = sitting on the thumb, so it takes the inverted text colour; inactive sits on the
   track and only dims. */
.icon {
  color: var(--ui-text-dimmed);
  transition:
    color 320ms ease,
    opacity 320ms ease;
}

.icon-sun {
  color: var(--ui-text-inverted);
}

.icon-moon {
  opacity: 0.8;
}

.theme-toggle.is-dark .icon-sun {
  color: var(--ui-text-dimmed);
  opacity: 0.8;
}

.theme-toggle.is-dark .icon-moon {
  color: var(--ui-text-inverted);
  opacity: 1;
}

.icon-art {
  transform-box: fill-box;
  transform-origin: center;
  transform: scale(0.85);
  transition: transform 320ms cubic-bezier(0.4, 0.15, 0.2, 1.2);
}

.icon-sun .icon-art,
.theme-toggle.is-dark .icon-moon .icon-art {
  transform: scale(1);
}

.theme-toggle.is-dark .icon-sun .icon-art {
  transform: scale(0.85);
}

.sun-core {
  fill: currentcolor;
}

.sun-rays {
  transform-box: fill-box;
  transform-origin: center;
  transition: transform 420ms ease;
}

.sun-rays line {
  stroke: currentcolor;
  stroke-linecap: round;
  stroke-width: 1.5;
}

/* The rays swing a notch as the thumb passes, so the handover reads as one motion. */
.theme-toggle.is-dark .sun-rays {
  transform: rotate(45deg);
}

.icon-moon path {
  fill: currentcolor;
}

@media (prefers-reduced-motion: reduce) {
  .thumb,
  .icon,
  .icon-art,
  .sun-rays {
    transition-duration: 0.01ms;
  }
}
</style>
